// src/oura/components/docs/write/ShareView.tsx
//
// Share with the client (B17), a view inside the doc drawer. First the
// pre-share checklist, 3 checks always shown:
//   link sharing on     must be fixed: "Fix it" turns it off
//   edit access         must be fixed: "Fix it" makes them commenters
//   our internal notes  a warning: a person deletes them in the doc
// Then who it goes to: comment-only, to the client's addresses Oura suggests
// (onboarding's access contact and approver), or one typed in. After it is
// shared, who it is shared with shows here, and the client hears in the
// bell in Oura only: no email from Oura or Google. The review email is its
// own step, sent by a named person, and is offered next.

import { useMemo, useState, type ReactNode } from "react";
import { CheckIcon, ExternalLinkIcon, ShieldAlertIcon, TriangleAlertIcon } from "lucide-react";
import { readDocsFolderNow, setDocumentReplyBy } from "@service/aeo/clientDocs";
import { defaultReplyBy, myDate } from "@service/aeo/clientDocsRules";
import { fixShareCheck, shareDocument, type ShareCheck, type ShareCheckItem, type ShareFixCode } from "@service/aeo/clientDocsWrite";
import type { StaffDocument } from "@service/aeo/clientDocs";
import { cn } from "../../../lib/cn";
import { DrawerBlock, DrawerFoot, DrawerNotice, DrawerPulse, PillButton, PillLink, SURFACE, TYPE, type DrawerFootMessage } from "@oura/ui";
import { docDate, docsFailureText, plural } from "@oura/docs";
import { isEmail } from "../../../onboarding/drafts";
import { inputClass } from "../../../onboarding/messageText";
import { reviewOpen, useShareCheck, type DocWriteView } from "./model";
import { CHECKS, FIXABLE, unknownChecks } from "./shareChecks";



const ROLE_WORD: Record<string, string> = { reader: "can view", commenter: "can comment", writer: "can edit", owner: "owns it" };

function CheckRow({
    okText,
    item,
    docUrl,
    fixing,
    disabled,
    onFix,
}: {
    /** What it says when it passes; null for a check this screen does not know,
     *  which only ever appears because the server sent an item for it. */
    okText: string | null;
    item: ShareCheckItem | null;
    docUrl: string;
    fixing: boolean;
    disabled: boolean;
    onFix: (code: ShareFixCode) => void;
}) {
    // One click puts some of them right; the rest are fixed in the doc, so the
    // row offers the doc instead of a button that does nothing.
    const oneClick = item && FIXABLE.has(item.code) ? (item.code as ShareFixCode) : null;
    const level = item ? item.level ?? "fix" : null;
    const Icon = level === "fix" ? ShieldAlertIcon : level === "warn" ? TriangleAlertIcon : CheckIcon;
    return (
        <div className={cn("flex items-start gap-2 border-t py-2.5 first:border-t-0 first:pt-1", SURFACE.hairline)}>
            <Icon aria-hidden size={14} className={cn("mt-[3px] shrink-0", level === "fix" ? "text-rose-600" : level === "warn" ? "text-amber-600" : "text-emerald-600")} />
            <div className="min-w-0 flex-1">
                <p className={cn(TYPE.tb, "m-0 break-words", level === "fix" ? "font-medium text-rose-700" : level === "warn" ? "font-medium text-amber-800" : "text-gray-900")}>
                    {item ? item.message : okText}
                </p>
                {item?.detail && item.detail.length > 0 && (
                    <ul className={cn(TYPE.tc, "m-0 mt-1 list-plain space-y-0.5 p-0 text-gray-600")}>
                        {item.detail.map((d) => (
                            <li key={d} className="break-words">
                                {d}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {level === "fix" && oneClick && (
                <PillButton size="sm" busy={fixing} writes disabled={disabled} onClick={() => onFix(oneClick)}>
                    Fix it
                </PillButton>
            )}
            {level === "warn" || (level === "fix" && !oneClick) ? (
                <PillLink size="sm" href={docUrl} target="_blank" rel="noopener noreferrer" icon={ExternalLinkIcon}>
                    Open the doc<span className="sr-only">, opens in a new tab</span>
                </PillLink>
            ) : null}
        </div>
    );
}

export function ShareView({
    doc,
    businessId,
    onBusy,
    onView,
}: {
    doc: StaffDocument;
    businessId: string;
    onBusy: (busy: boolean) => void;
    onView: (view: DocWriteView | "doc") => void;
}) {
    const load = useShareCheck(doc.id);
    const check: ShareCheck | null = load.data;
    const already = useMemo(() => doc.sharing.sharedWith.filter((p) => !p.ours && p.role !== "owner"), [doc.sharing.sharedWith]);
    const alreadyEmails = useMemo(() => new Set(already.map((p) => (p.email ?? "").toLowerCase()).filter(Boolean)), [already]);

    /** Addresses offered and picked; a typed one joins the list. */
    const [extra, setExtra] = useState<string[]>([]);
    const [unpicked, setUnpicked] = useState<Set<string>>(() => new Set());
    const [typed, setTyped] = useState("");
    const [typedError, setTypedError] = useState<string | null>(null);
    const [busy, setBusy] = useState<"share" | "read" | ShareFixCode | null>(null);
    const [message, setMessage] = useState<DrawerFootMessage | null>(null);
    const [sharedNow, setSharedNow] = useState<string[] | null>(null);
    /** The reply-by the client is given, seen and changed before sharing: the doc's own, else the default from today. */
    const [replyBy, setReplyBy] = useState<string>(() => doc.replyBy ?? defaultReplyBy(new Date().toISOString()));
    const [replyByError, setReplyByError] = useState<string | null>(null);
    /** The client received it before Oura (backend #6553): shared without a bell, dated the day it was marked Done. */
    const [alreadySent, setAlreadySent] = useState(false);

    const offered = Array.from(new Set([...(check?.suggestedEmails ?? []), ...extra].map((e) => e.trim().toLowerCase()))).filter((e) => e && !alreadyEmails.has(e));
    const picked = offered.filter((e) => !unpicked.has(e));
    // Our internal note and a name that disagrees with the title hold Share
    // too: one click would show the client our note, or the wrong doc's name.
    const notesHeld = Boolean(check?.items.some((i) => i.code === "internal_notes"));
    const nameFlag = doc.flags.find((f) => f.code === "name_title_disagree") ?? null;
    const held = notesHeld || Boolean(nameFlag);
    const blocked = check ? !check.ok || held : true;

    const toggle = (email: string) =>
        setUnpicked((was) => {
            const next = new Set(was);
            if (next.has(email)) next.delete(email);
            else next.add(email);
            return next;
        });

    const addTyped = () => {
        const e = typed.trim().toLowerCase();
        if (!e) return;
        if (!isEmail(e)) {
            setTypedError(`Check this address: ${typed.trim()}`);
            return;
        }
        if (/@ourteam\.ai$/i.test(e)) {
            setTypedError("Share to the client's address, not an ourteam one.");
            return;
        }
        setExtra((x) => [...x, e]);
        setUnpicked((was) => {
            const next = new Set(was);
            next.delete(e);
            return next;
        });
        setTyped("");
        setTypedError(null);
    };

    const fix = async (code: ShareFixCode) => {
        setBusy(code);
        onBusy(true);
        const r = await fixShareCheck(doc.id, code);
        setBusy(null);
        onBusy(false);
        setMessage(r.ok ? { tone: "success", text: code === "link_sharing" ? "Link sharing is off." : "They can comment now, not edit." } : { tone: "error", text: docsFailureText(r, "the document") });
    };

    /** Once the note is deleted or the name fixed in Drive: Oura reads the folder, then the checks again. */
    const readAgain = async () => {
        setBusy("read");
        onBusy(true);
        const r = await readDocsFolderNow(businessId);
        setBusy(null);
        onBusy(false);
        if (!r.ok) return setMessage({ tone: "error", text: docsFailureText(r, "the folder") });
        load.reload();
        setMessage({ tone: "info", text: "Oura is reading the folder again. The checks update when it has." });
    };

    const share = async () => {
        // Refused in words, never by a missing or greyed button.
        if (picked.length === 0) return setMessage({ tone: "error", text: "Tick the client's address, or add it, first." });
        if (blocked) return setMessage({ tone: "error", text: held ? "Delete the internal note and fix the name first." : "Fix the checks in red first." });
        const firstShare = already.length === 0;
        // A first share always carries a reply-by: an empty or past date is refused at its field, never sent.
        if (firstShare && (!/^\d{4}-\d{2}-\d{2}$/.test(replyBy) || replyBy < myDate(new Date()))) {
            setReplyByError(replyBy ? "Pick a reply-by date that is today or later." : "Pick a reply-by date first.");
            return;
        }
        setBusy("share");
        onBusy(true);
        try {
            // The date they were shown is saved before the share, so the client's
            // bell and the doc carry it from the first moment. Saved even when it is
            // the default: the server's own default counts from its share time, which
            // can fall on another day than the one shown here.
            if (firstShare && doc.replyBy !== replyBy) {
                const dated = await setDocumentReplyBy(doc.id, replyBy);
                if (!dated.ok) {
                    if (dated.code === "reply_by_in_past") setReplyByError("Pick a reply-by date that is today or later.");
                    setMessage({ tone: "error", text: `Not shared yet: the reply-by did not save. ${docsFailureText(dated, "the document")}` });
                    return;
                }
            }
            const emails = picked;
            const quiet = firstShare && alreadySent;
            const r = await shareDocument(doc.id, emails, quiet ? { alreadySent: true } : undefined);
            if (!r.ok) {
                setMessage({ tone: "error", text: docsFailureText(r, "the document") });
                return;
            }
            setSharedNow(emails);
            setExtra([]);
            setMessage({
                tone: "success",
                text: quiet
                    ? `Shared quietly with ${emails.join(", ")}, comment-only. They were not notified. Reply by ${docDate(replyBy) ?? replyBy}.`
                    : `Shared with ${emails.join(", ")}, comment-only.${firstShare ? ` Reply by ${docDate(replyBy) ?? replyBy}.` : ""}`,
            });
        } catch {
            setMessage({ tone: "error", text: "That did not go through. Try again." });
        } finally {
            setBusy(null);
            onBusy(false);
        }
    };

    /* The foot: Share, or after it the next step, the review email. */
    const back = (
        <PillButton disabled={busy !== null} onClick={() => onView("doc")}>
            Back to the doc
        </PillButton>
    );
    let first: ReactNode = null;
    if (sharedNow) {
        if (reviewOpen({ ...doc, sharing: { ...doc.sharing, clientCanSee: true } })) {
            first = (
                <PillButton variant="primary" writes onClick={() => onView("review")}>
                    Write the review email
                </PillButton>
            );
        }
    } else if (check) {
        first = (
            <PillButton variant="primary" busy={busy === "share"} writes disabled={busy !== null || held} onClick={() => void share()}>
                {picked.length === 0 ? "Share" : `Share with ${picked.length === 1 ? picked[0] : plural(picked.length, "address", "addresses")}`}
            </PillButton>
        );
    }

    const clientWord = already[0]?.name ?? "The client";

    return (
        <>
            {load.error && check && <DrawerNotice action={<PillButton variant="text" onClick={load.reload}>Try again</PillButton>}>{docsFailureText(load.error, "the sharing")}</DrawerNotice>}

            <DrawerBlock label={already.length > 0 ? "Sharing checks" : "Before it's shared"} busy={busy !== null && busy !== "share"}>
                {!check ? (
                    load.status === "loading" ? (
                        <DrawerPulse lines={3} label="Checking the sharing" />
                    ) : (
                        <p className="m-0 mt-1 text-gray-700">{load.error ? docsFailureText(load.error, "the sharing") : "Oura could not check the sharing."}</p>
                    )
                ) : (
                    <div className="mt-1">
                        {CHECKS.map((c) => (
                            <CheckRow
                                key={c.code}
                                okText={c.ok}
                                item={check.items.find((i) => i.code === c.code) ?? null}
                                docUrl={doc.url}
                                fixing={busy === c.code}
                                disabled={busy !== null}
                                onFix={(code) => void fix(code)}
                            />
                        ))}
                        {/* Whatever else the server sent. Last, after the
                            checks that have a settled order, and with no
                            "passes" row of its own: this screen cannot word a
                            pass for a check it does not know. */}
                        {unknownChecks(check.items).map((i) => (
                            <CheckRow
                                key={i.code}
                                okText={null}
                                item={i}
                                docUrl={doc.url}
                                fixing={busy === i.code}
                                disabled={busy !== null}
                                onFix={(code) => void fix(code)}
                            />
                        ))}
                        {nameFlag && (
                            <div className={cn("flex items-start gap-2 border-t py-2.5", SURFACE.hairline)}>
                                <TriangleAlertIcon aria-hidden size={14} className="mt-[3px] shrink-0 text-amber-600" />
                                <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1 break-words font-medium text-amber-800")}>{nameFlag.message} Fix the name or the title in the doc first.</p>
                            </div>
                        )}
                        {held && (
                            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                                <p className={cn(TYPE.tc, "m-0 font-medium text-rose-700")}>Share waits until {notesHeld && nameFlag ? "both are" : "this is"} fixed in the doc.</p>
                                <PillButton variant="text" busy={busy === "read"} writes disabled={busy !== null} onClick={() => void readAgain()}>
                                    Read the folder again
                                </PillButton>
                            </div>
                        )}
                    </div>
                )}
            </DrawerBlock>

            {already.length > 0 && (
                <DrawerBlock label={`Shared with · ${already.length}`}>
                    <ul className="m-0 mt-1 list-plain space-y-1 p-0">
                        {already.map((p, i) => (
                            <li key={p.email ?? p.name ?? i} className={cn(TYPE.tb, "break-words text-gray-900")}>
                                {p.name && p.email ? `${p.name} (${p.email})` : p.email ?? p.name ?? "Someone"}
                                <span className="text-gray-500">, {ROLE_WORD[p.role] ?? p.role}</span>
                            </li>
                        ))}
                    </ul>
                    {docDate(doc.sharing.sharedAt) && <p className={cn(TYPE.tc, "m-0 mt-1.5 text-gray-500")}>Shared {docDate(doc.sharing.sharedAt)}</p>}
                </DrawerBlock>
            )}

            {check && !sharedNow && (
                <DrawerBlock label={already.length > 0 ? "Also share with" : "Share comment-only with"}>
                    {offered.length > 0 ? (
                        <div className="mt-1 space-y-1">
                            {offered.map((e) => (
                                <label key={e} className={cn(TYPE.tb, "flex min-h-9 cursor-pointer items-center gap-2 break-all text-gray-900")}>
                                    <input type="checkbox" className="h-4 w-4 shrink-0 accent-blue-600" checked={!unpicked.has(e)} onChange={() => toggle(e)} disabled={busy !== null} />
                                    {e}
                                </label>
                            ))}
                        </div>
                    ) : already.length > 0 ? (
                        <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>Every address Oura knows for the client already has it.</p>
                    ) : (
                        // NOTHING TO OFFER IS TWO DIFFERENT FACTS, and saying
                        // the wrong one leaves staff stuck. "Already has it"
                        // over a doc shared with nobody is simply untrue, and
                        // it reads as "you are done" while Share stays refused
                        // with nothing to tick (#4051).
                        //
                        // The server builds `suggestedEmails` from the
                        // client's onboarding and leaves out our own
                        // addresses, so a client recorded with an ourteam.ai
                        // contact comes back empty -- which is what this says,
                        // along with where to fix it.
                        <p role="status" className={cn(TYPE.tc, "m-0 mt-1 font-medium text-amber-800")}>
                            Oura has no client address for this business. It reads them from the client&rsquo;s
                            onboarding &mdash; the access contact and the approver &mdash; and an ourteam.ai address is
                            not one. Add the client&rsquo;s own address below, or fix it on their onboarding.
                        </p>
                    )}
                    <div className="mt-2 flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                            <input
                                className={inputClass(typedError)}
                                value={typed}
                                type="email"
                                aria-label="Add the client's email"
                                aria-invalid={Boolean(typedError)}
                                placeholder="Add the client's email"
                                onChange={(e) => {
                                    setTyped(e.target.value);
                                    setTypedError(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addTyped();
                                    }
                                }}
                                disabled={busy !== null}
                            />
                            {typedError && <p role="alert" className={cn(TYPE.tc, "m-0 mt-1.5 font-medium text-rose-600")}>{typedError}</p>}
                        </div>
                        <PillButton className="h-10" disabled={busy !== null || !typed.trim()} onClick={addTyped}>
                            Add
                        </PillButton>
                    </div>
                    {!already.length && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <label htmlFor={`${doc.id}-reply-by`} className={cn(TYPE.tb, "text-gray-900")}>
                                Reply by
                            </label>
                            <input
                                id={`${doc.id}-reply-by`}
                                type="date"
                                value={replyBy}
                                min={myDate(new Date())}
                                onChange={(e) => {
                                    setReplyBy(e.target.value);
                                    setReplyByError(null);
                                }}
                                disabled={busy !== null}
                                required
                                aria-invalid={Boolean(replyByError)}
                                aria-describedby={replyByError ? `${doc.id}-reply-by-error` : undefined}
                                className={cn(inputClass(replyByError), "w-auto")}
                            />
                            <span className={cn(TYPE.tc, "text-gray-500")}>{docDate(replyBy)}</span>
                            {replyByError && (
                                <p id={`${doc.id}-reply-by-error`} role="alert" className={cn(TYPE.tc, "m-0 w-full font-medium text-rose-600")}>
                                    {replyByError}
                                </p>
                            )}
                        </div>
                    )}
                    {!already.length && (
                        // A doc the client got before Oura (Lush's Word files, backend #6553):
                        // they get access and it shows on their Documents page, with no new bell.
                        <div className="mt-3 flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <p id={`${doc.id}-already-sent`} className={cn(TYPE.tb, "m-0 text-gray-900")}>
                                    Already sent to the client before Oura
                                </p>
                                <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                                    They get access and it shows on their Documents page, without a new notification.
                                    {docDate(doc.done?.at ?? null) ? ` Dated ${docDate(doc.done?.at ?? null)}, the day it was marked Done.` : ""}
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={alreadySent}
                                aria-labelledby={`${doc.id}-already-sent`}
                                disabled={busy !== null}
                                onClick={() => setAlreadySent((v) => !v)}
                                className={cn(
                                    "relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                                    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
                                    alreadySent ? "bg-blue-600" : "bg-gray-300",
                                )}
                            >
                                <span aria-hidden className={cn("inline-block h-5 w-5 rounded-full bg-white shadow transition-transform", alreadySent ? "translate-x-[22px]" : "translate-x-0.5")} />
                            </button>
                        </div>
                    )}
                    {blocked && !held && picked.length > 0 && <p className={cn(TYPE.tc, "m-0 mt-2 font-medium text-rose-700")}>Fix the red items first.</p>}
                </DrawerBlock>
            )}

            <p className={cn(TYPE.tc, "m-0 px-1 text-gray-500")}>
                {alreadySent && !already.length
                    ? `${clientWord} is not notified: they got it before Oura. The review email is the next step.`
                    : `${clientWord} hears in the bell in Oura. The review email is the next step.`}
            </p>

            <DrawerFoot message={message} first={first} second={back} />
        </>
    );
}
